import { useQuery } from '@tanstack/react-query';
import { LivestreamCard } from './components';
import { getActiveLivestreams, getScheduledLivestreams } from '../../api/livestream';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../api/firebase';
import { MenuBar } from '../../components/ui/glow-menu';
import { StickyFooter } from '../../components/ui/sticky-footer';
import { Home, Video, Handshake, Radio, LogIn, Filter } from 'lucide-react';
import logoImg from '../../assets/images/logo.svg';

const menuItems = [
  { icon: Home, label: 'Início', href: '/videos', gradient: 'radial-gradient(circle, rgba(245,138,37,0.2) 0%, transparent 100%)', iconColor: 'text-orange-500' },
  { icon: Video, label: 'Vídeos', href: '/videos', gradient: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 100%)', iconColor: 'text-blue-500' },
  { icon: Radio, label: 'Ao Vivo', href: '/livestream', gradient: 'radial-gradient(circle, rgba(239,68,68,0.15) 0%, transparent 100%)', iconColor: 'text-red-500' },
  { icon: Handshake, label: 'Contratar', href: '/contract-camera', gradient: 'radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 100%)', iconColor: 'text-green-500' },
  { icon: LogIn, label: 'Sair', href: '/login', gradient: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 100%)', iconColor: 'text-purple-500' },
];

type TabType = 'live' | 'scheduled';

function Livestream() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('live');
  const [selectedArena, setSelectedArena] = useState<string>('all');
  const [selectedCourt, setSelectedCourt] = useState<string>('all');

  const { data: activeLivestreams, isLoading: isLoadingActive } = useQuery({
    queryKey: ['getActiveLivestreams'],
    queryFn: getActiveLivestreams,
    refetchInterval: 30000,
  });

  const { data: scheduledLivestreams, isLoading: isLoadingScheduled } = useQuery({
    queryKey: ['getScheduledLivestreams'],
    queryFn: getScheduledLivestreams,
  });

  const isLoading = activeTab === 'live' ? isLoadingActive : isLoadingScheduled;
  const rawLivestreams = activeTab === 'live' ? activeLivestreams : scheduledLivestreams;

  const availableArenas = useMemo(() => {
    const allStreams = [...(activeLivestreams || []), ...(scheduledLivestreams || [])];
    const arenaMap = new Map();
    allStreams.forEach((stream: any) => {
      if (!arenaMap.has(stream.arenaName)) {
        arenaMap.set(stream.arenaName, { name: stream.arenaName, courts: new Set() });
      }
      arenaMap.get(stream.arenaName).courts.add(stream.courtName);
    });
    return Array.from(arenaMap.values()).map((arena: any) => ({
      ...arena,
      courts: Array.from(arena.courts),
    }));
  }, [activeLivestreams, scheduledLivestreams]);

  const availableCourts = useMemo(() => {
    if (selectedArena === 'all') return [];
    const arena = availableArenas.find((a: any) => a.name === selectedArena);
    return arena ? arena.courts : [];
  }, [selectedArena, availableArenas]);

  const livestreams = useMemo(() => {
    if (!rawLivestreams) return [];
    return rawLivestreams.filter((stream: any) => {
      if (selectedArena !== 'all' && stream.arenaName !== selectedArena) return false;
      if (selectedCourt !== 'all' && stream.courtName !== selectedCourt) return false;
      return true;
    });
  }, [rawLivestreams, selectedArena, selectedCourt]);

  const handleArenaChange = (arena: string) => {
    setSelectedArena(arena);
    setSelectedCourt('all');
  };

  const handleMenuClick = (label: string) => {
    if (label === 'Sair') {
      signOut(auth).finally(() => {
        localStorage.removeItem('accessToken');
        navigate('/login');
      });
      return;
    }
    const item = menuItems.find((i) => i.label === label);
    if (item) navigate(item.href);
  };

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Navbar */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 py-3 backdrop-blur-xl border-b border-white/5"
        style={{ backgroundColor: 'rgba(10,10,10,0.8)' }}
      >
        <img src={logoImg} alt="Apertai" className="hidden md:block" style={{ width: '127px' }} />
        <MenuBar items={menuItems} activeItem="Ao Vivo" onItemClick={handleMenuClick} />
      </header>

      {/* Content */}
      <div className="flex-1 pt-40 px-4 md:px-12 pb-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="relative">
                <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
                <div className="absolute inset-0 h-3 w-3 rounded-full bg-red-500 animate-ping opacity-50" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Livestreams</h1>
            </div>
            <p className="text-zinc-500">
              Assista às transmissões ao vivo das quadras ou confira as próximas lives agendadas
            </p>
          </div>

          {/* Filters */}
          {availableArenas.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <Filter className="h-4 w-4" />
                <span>Filtrar:</span>
              </div>
              <select
                className="h-10 px-3 rounded-lg text-sm border border-zinc-700 bg-zinc-900 text-zinc-300 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                style={{ colorScheme: 'dark' }}
                value={selectedArena}
                onChange={(e) => handleArenaChange(e.target.value)}
              >
                <option value="all">Todas as arenas</option>
                {availableArenas.map((arena: any) => (
                  <option key={arena.name} value={arena.name}>{arena.name}</option>
                ))}
              </select>
              {availableCourts.length > 0 && (
                <select
                  className="h-10 px-3 rounded-lg text-sm border border-zinc-700 bg-zinc-900 text-zinc-300 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                  style={{ colorScheme: 'dark' }}
                  value={selectedCourt}
                  onChange={(e) => setSelectedCourt(e.target.value)}
                >
                  <option value="all">Todas as quadras</option>
                  {availableCourts.map((court: string) => (
                    <option key={court} value={court}>{court}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-8">
            <button
              onClick={() => setActiveTab('live')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border-none cursor-pointer"
              style={
                activeTab === 'live'
                  ? { background: 'linear-gradient(135deg, #dc2626, #ef4444)', color: 'white' }
                  : { background: 'transparent', color: '#a1a1aa', border: '1px solid #3f3f46' }
              }
            >
              <span className="h-2 w-2 rounded-full bg-red-400 animate-pulse" />
              Ao Vivo
              {activeLivestreams && activeLivestreams.length > 0 && (
                <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full ml-1">
                  {activeLivestreams.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('scheduled')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border-none cursor-pointer"
              style={
                activeTab === 'scheduled'
                  ? { background: 'linear-gradient(135deg, #e57605, #f29b2a)', color: 'white' }
                  : { background: 'transparent', color: '#a1a1aa', border: '1px solid #3f3f46' }
              }
            >
              Agendadas
              {scheduledLivestreams && scheduledLivestreams.length > 0 && (
                <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full ml-1">
                  {scheduledLivestreams.length}
                </span>
              )}
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="text-center py-20">
              <p className="text-zinc-500">Carregando livestreams...</p>
            </div>
          ) : !livestreams || livestreams.length === 0 ? (
            <div className="text-center py-20">
              <Radio className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {activeTab === 'live' ? 'Nenhuma live ao vivo no momento' : 'Nenhuma live agendada'}
              </h3>
              <p className="text-zinc-500">
                {activeTab === 'live' ? 'Confira as lives agendadas ou volte mais tarde' : 'Em breve teremos novas transmissões'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {livestreams.map((livestream: any) => (
                <LivestreamCard key={livestream.id} livestream={livestream} isLive={activeTab === 'live'} />
              ))}
            </div>
          )}
        </div>
      </div>

      <StickyFooter />
    </div>
  );
}

export default Livestream;
